'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { IRegionShort, IRegionFeaturedResort } from '@/api/region/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { useRegionLayoutBubble } from '@/page-components/region/_context';
import { IRegionLayout } from '@/page-components/region/_layout';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { ILiftTicketsResponse } from '@/components/region/LiftTicketTable/types';
import { LoadMoreRows } from '@/components/region/LoadMoreRows';
import { useLoadMoreResorts } from '@/components/region/useLoadMoreResorts';
import { fetchRegionLiftTicketsPricesByResort } from '@/api/region/static';
import { REGION_RESORT_PAGE_SIZE } from '@/ots-constants';
import ToExternalPurchasePageButton from '@/components/region/LiftTicketTable/components/ToExternalPurchasePageButton';
import { SkipassesLiftTicketsInfo } from '@/components/region/SkipassesLiftTicketsInfo';
import { Routes } from '@/routes';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from '@/components/400';
import { IRelatedNews } from '@/api/resort/types';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { DataTable } from '@/components/common/DataTable';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';

export type RegionLiftTicketsPageProps = {
  resortPricesList: ILiftTicketsResponse,
  numResorts: number,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
  featured: IRegionFeaturedResort,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-lift-tickets-page';
export const TEMPLATE_ID = '2.1.6';
export const TEMPLATE_NAME = 'Region Lift Tickets';

export default function RegionLiftTicketsPage({
  resortPricesList,
  pathInfo, numResorts, relatedNews,
}: RegionLiftTicketsPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resortPricesList) {
    return <Hero400Error light />;
  }

  const seoText = pathInfo.isLogical ? m('region.liftTickets.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.liftTickets.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  const seoTitle = pathInfo.isLogical ? t('region.liftTickets.logicSeoTitle',
    { region: pathInfo.title }) : t('region.liftTickets.seoTitle', { region: pathInfo.title });

  const fetchPage = React.useCallback(
    (page: number) => fetchRegionLiftTicketsPricesByResort(
      pathInfo.uuid, page, REGION_RESORT_PAGE_SIZE,
    ),
    [pathInfo.uuid],
  );
  const {
    rows, hasMore, isLoading, loadMore,
  } = useLoadMoreResorts({
    initial: resortPricesList,
    fetchPage,
    label: `region ${pathInfo.uuid} lift-tickets`,
  });
  const hasResorts = rows.length > 0;

  // When every row reports the same currency we pull it out and
  // surface it as a single line above the table so the price columns
  // stay tidy.
  const commonCurrency = useMemo(() => {
    if (!rows.length) return null;
    const first = rows[0].currency;
    return rows.every((r) => r.currency === first) ? first : null;
  }, [rows]);

  const dash = t('common.dash');

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={seoTitle}>{seoText}</SeoSection>

          {hasResorts ? (
            <section>
              <Heading
                level={2}
                className="mb-4"
              >
                {t('region.liftTickets.skiPasses', { region: pathInfo.title })}
              </Heading>
              {commonCurrency && (
                <Text variant="subtle" className="mb-2 text-xs text-muted-foreground">
                  {`${t('resort.liftTickets.currencyText')} ${commonCurrency}`}
                </Text>
              )}
              <DataTable minWidth={760}>
                <DataTable.Head>
                  <DataTable.HeadRow>
                    <DataTable.HeadCell>
                      {t('region.liftTickets.resort')}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell align="right">
                      {`${t('region.liftTickets.season')} ${t('resort.liftTickets.adult')}`}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell align="right">
                      {`${t('region.liftTickets.season')} ${t('resort.liftTickets.child')}`}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell align="right">
                      {`${t('resort.liftTickets.weekday')} ${t('resort.liftTickets.adult')}`}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell align="right">
                      {`${t('resort.liftTickets.weekday')} ${t('resort.liftTickets.child')}`}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell align="right">
                      {t('common.purchase')}
                    </DataTable.HeadCell>
                  </DataTable.HeadRow>
                </DataTable.Head>
                <DataTable.Body>
                  {rows.map((r, i) => (
                    <DataTable.Row key={r.resort.uuid} zebra={i % 2 === 0}>
                      <DataTable.RowHeader>
                        <Link
                          href={Routes.resortLiftTickets(r.resort)}
                          className="hover:text-primary hover:underline"
                        >
                          {r.resort.title}
                        </Link>
                        {!commonCurrency && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {`${t('region.liftTickets.currencyText')} ${r.currency}`}
                          </span>
                        )}
                      </DataTable.RowHeader>
                      <DataTable.Cell align="right">
                        {r.seasonPassAdult || dash}
                      </DataTable.Cell>
                      <DataTable.Cell align="right">
                        {r.seasonPassChild || dash}
                      </DataTable.Cell>
                      <DataTable.Cell align="right">
                        {r.dailyPassAdult || dash}
                      </DataTable.Cell>
                      <DataTable.Cell align="right">
                        {r.dailyPassChild || dash}
                      </DataTable.Cell>
                      <DataTable.Cell align="right">
                        {r.purchaseUrl ? (
                          <ToExternalPurchasePageButton
                            url={r.purchaseUrl}
                            resort={r.resort}
                          />
                        ) : dash}
                      </DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable>
              <LoadMoreRows hasMore={hasMore} isLoading={isLoading} onLoadMore={loadMore} />
              <Text variant="subtle" className="mt-3 text-xs text-muted-foreground">
                {t('region.liftTickets.disclaimer')}
              </Text>
            </section>
          ) : (
            <Text variant="default" className="font-semibold text-muted-foreground">
              {t('region.closingDates.noResorts', { region: pathInfo.title })}
            </Text>
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
            regionId={pathInfo.uuid}
          />

          <SkipassesLiftTicketsInfo
            header={t('region.liftTickets.passSeoInfoTitle', { region: pathInfo.title })}
            seoText={t('region.liftTickets.passSeoInfoText', { region: pathInfo.title })}
          />

          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd isRegion id={pathInfo.uuid} hideWeSkiOnMobile />
      </Side>
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
